import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pad } from 'viem';
import { useWalletClient } from 'wagmi';
import { TokenMessengerV2Abi } from '../abis/TokenMessengerV2Abi';
import { getCctpConfig, getCctpDomain } from '../config/cctp';

export interface CctpBurnParams {
  /** Amount of USDC to burn, in USDC base units (6 decimals) */
  amount: bigint;
  /** EVM chain ID of the destination chain (where USDC will be minted) */
  destinationChainId: number;
  /**
   * Address to receive the minted USDC on the destination chain.
   * Defaults to the connected wallet address if not provided.
   */
  mintRecipient?: `0x${string}`;
}

export interface CctpBurnResult {
  /** Transaction hash of the depositForBurn call */
  txHash: `0x${string}`;
}

/**
 * Returns a mutation function to execute `depositForBurn` on the CCTP
 * TokenMessengerV2 contract on the source chain.
 *
 * This initiates a cross-chain USDC transfer: USDC is burned on the source
 * chain, and the Circle Attestation Service issues an attestation that allows
 * `receiveMessage` to be called on the destination chain to mint USDC.
 *
 * The connected wallet must be on `sourceChainId` when the mutation is called.
 *
 * @param sourceChainId - EVM chain ID where the burn happens (source)
 */
export function useCctpBurn(sourceChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({ chainId: sourceChainId });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CctpBurnParams): Promise<CctpBurnResult> => {
      if (!sourceChainId) {
        throw new Error('sourceChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const sourceConfig = getCctpConfig(sourceChainId);
      const destinationDomain = getCctpDomain(params.destinationChainId);

      const recipient = params.mintRecipient ?? walletClient.account.address;

      // CCTP expects the recipient as a bytes32 (left-padded EVM address)
      const mintRecipientBytes32 = pad(recipient, { size: 32 });

      // CCTP V2 depositForBurn has 7 params (vs V1's 4)
      const ZERO_BYTES32 = '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`;

      const txHash = await walletClient.writeContract({
        address: sourceConfig.tokenMessenger,
        abi: TokenMessengerV2Abi,
        functionName: 'depositForBurn',
        args: [
          params.amount,
          destinationDomain,
          mintRecipientBytes32,
          sourceConfig.usdcAddress,
          ZERO_BYTES32,  // destinationCaller: anyone can call receiveMessage
          BigInt("0"),            // maxFee: 0 = standard (Circle charges minimum)
          2000,          // minFinalityThreshold: 2000 = standard finality
        ],
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return { txHash };
    },
    onSuccess: () => {
      // Invalidate USDC balance after burn
      queryClient.invalidateQueries({
        queryKey: ['sourceTokenBalance', sourceChainId],
      });
    },
  });
}
